import DialogModal from "../../../atoms/dialog";
import useAgendaData from "../../multi-agenda/hooks/usePeAgenda";
import { Box, Card, CardContent, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import useAgendaNavigation from "../../multi-agenda/hooks/useAgendaStateNavigation";
import SkeletonLoader from "../../../atoms/project-details/SkeletonLoader";
import { EmptyAgendaState } from "../../../organisms/accept-invitation-form/agenda-section";
import MobileAgendaAccordion from "../../multi-agenda/MobileAgendaAccordion";
import AgendaHeader from "../../multi-agenda/AgendaHeader";
import AgendaDetails from "../../multi-agenda/AgendaDetails";
import { AgendaList } from "../../multi-agenda/AgendaList";
import { isExpert, isOnlyAdmins, isSuperAdmin } from "../../../utils/role";
import { TExpert_Invitation } from "../../../constants/expert-project.constants";
import CustomBtnFilled from "../../../atoms/form-molecules/CustomBtnFilled";
import { handleDirectCopyAgendaToClipboard } from "../../../organisms/project/project-agenda/helper";
import { useSnackbar } from "notistack";

interface Props {
  isOpen: boolean;
  handleClose: () => void;
  peId: number;
  projectId: number;
  refetch: () => Promise<void>;
  expertId?: number;
  expertInvitationStatus: TExpert_Invitation;
  agendaId: number | null;
  openRespondedAgenda?: boolean
}

function AgendaModal({ isOpen, handleClose, peId, projectId, refetch, expertId, expertInvitationStatus, agendaId, openRespondedAgenda = false }: Props) {

  const theme = useTheme();
  const isUserExpert = isExpert()
  const { enqueueSnackbar } = useSnackbar();
  const expert_id = isUserExpert ? localStorage.getItem("expert_id") : expertId;
  if (!expert_id) {
    throw new Error("Expert ID not found");
  }
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { peAgendaData, setPeAgendaData, loading, setLoading } = useAgendaData(
    String(peId),
    String(projectId),
  );
  const archivedAgendaList = [
    ...peAgendaData?.declineAgenda,
    ...peAgendaData?.respondedByAdminAgenda,
    ...peAgendaData?.respondedByExpertAgenda
  ];
  // Use the initial agenda list based on openRespondedAgenda prop
  const initialAgendaList = openRespondedAgenda || peAgendaData?.sentAgenda?.length === 0 ? archivedAgendaList : peAgendaData?.sentAgenda;

  const selectedAgendaIdIndex = initialAgendaList.findIndex(
    (agenda) => agenda?.fk_agenda === Number(agendaId)
  );
  
  const {
    selectedAgendaIndex,
    setSelectedAgendaIndex,
    showArchived,
    expandedAccordion,
    setExpandedAccordion,
    toggleArchived,
  } = useAgendaNavigation(initialAgendaList.length, selectedAgendaIdIndex, openRespondedAgenda || peAgendaData?.sentAgenda?.length === 0);

  // Determine the current list based on showArchived state
  const displayedAgendaList = showArchived ? archivedAgendaList : peAgendaData?.sentAgenda;
  const selectedAgenda = displayedAgendaList?.[selectedAgendaIndex];
  return (
    <DialogModal isOpen={isOpen} handleClose={handleClose} title={"Agenda"} TitleEl={<>
      {isOnlyAdmins() && <Typography fontSize={"14px"} fontWeight={550}>
        {expertInvitationStatus && `Invitation Status: ${expertInvitationStatus}`}
      </Typography>}
    </>} >
      <Box mt={2}>
        {loading ? (
          <SkeletonLoader width="100%" height="500px" marginBottom="1.5em" />
        ) : isMobile ? (
          <Box sx={{ minHeight: '500px', display: 'flex', flexDirection: 'column' }}>
            {/* Mobile View */}
            <Card variant="outlined" sx={{ mb: 2, borderRadius: '10px' }}>
              <AgendaHeader showArchived={showArchived} onToggleArchived={toggleArchived} />
            </Card>

            {displayedAgendaList.length > 0 ? (
              <Box>
                {displayedAgendaList.map((agenda, index) => (
                  <MobileAgendaAccordion
                    key={agenda.id || index}
                    isAcceptInvitation={expertInvitationStatus === 'Accepted'}
                    agenda={agenda}
                    index={index}
                    isExpanded={expandedAccordion === `panel${index}`}
                    onToggle={() => setExpandedAccordion(`panel${index}`)}
                    showArchived={showArchived}
                    peId={+peId}
                    expertId={+expert_id}
                    projectId={+projectId}
                    setPeAgendaData={setPeAgendaData}
                    setLoading={setLoading}
                    handleNoSentAgendaExist={() => {
                      refetch()
                      handleClose()
                    }}
                  />
                ))}
              </Box>
            ) : (
              <Card variant="outlined" sx={{ borderRadius: '10px' }}>
                <CardContent>
                  <EmptyAgendaState showArchived={showArchived} />
                </CardContent>
              </Card>
            )}
          </Box>
        ) : (
          <Box sx={{ height: '500px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Desktop View */}
            <Grid container spacing={0} sx={{ flex: 1, height: '100%', overflow: 'hidden', margin: 0, width: '100%' }}>
              <Grid item xs={12} md={4} sx={{ height: '100%', paddingLeft: '0 !important', paddingTop: '0 !important' }}>
                <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: '10px' }}>
                  <AgendaHeader
                    showArchived={showArchived}
                    onToggleArchived={toggleArchived}
                  />
                  <AgendaList
                    agendas={displayedAgendaList}
                    selectedIndex={selectedAgendaIndex}
                    onSelectAgenda={setSelectedAgendaIndex}
                    showArchived={showArchived}
                  />
                </Card>
              </Grid>

              <Grid item xs={12} md={8} sx={{ height: '100%', paddingLeft: '16px !important', paddingTop: '0 !important' }}>
                <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: '10px' }}>
                  {selectedAgenda && (
                    <>
                      <AgendaHeader
                        showArchived={showArchived}
                        onToggleArchived={toggleArchived}
                        selectedIndex={selectedAgendaIndex}
                        totalCount={displayedAgendaList.length}
                        showToggleBtn={false}
                        ComponentForExtraInfo={
                          (isOnlyAdmins() && selectedAgenda?.status === 'RespondedByAdmin' || selectedAgenda?.status === 'RespondedByExpert') && <CustomBtnFilled
                            label="Copy to clipboard"
                            variant="outlined"
                            onClick={() => handleDirectCopyAgendaToClipboard(selectedAgenda.agenda_responses, enqueueSnackbar)}
                          />
                        }
                      />
                      <AgendaDetails
                        agenda={selectedAgenda}
                        showArchived={showArchived}
                        peId={+peId}
                        expertId={+expert_id}
                        projectId={+projectId}
                        setPeAgendaData={setPeAgendaData}
                        handleNoSentAgendaExist={() => {
                          const currentList = showArchived ? archivedAgendaList : peAgendaData?.sentAgenda;
                          if (currentList.length - 1 === 0) {
                            handleClose()
                          } else {
                            const newIndex = Math.min(selectedAgendaIndex, currentList.length - 1);
                            setSelectedAgendaIndex(newIndex);
                          }
                        }}
                        isAcceptInvitation={expertInvitationStatus === 'Accepted'}
                        showRevokeBtn={isSuperAdmin()}
                      />
                    </>
                  )}
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>
    </DialogModal>
  );
}

export default AgendaModal;